import { useState, useEffect } from 'react';
import { api } from '../services/api';

const AVATAR_EMOJIS = ['🧟','😰','🥊','⚔️','🧘'];
const AVATAR_NAMES  = ['Scrolling Zombie','Digital Slave','Screen Fighter','Mind Warrior','Digital Monk'];
const MAX_STREAK_DISPLAY = 10;

export default function Dashboard({ profile, setProfile, userId }) {
  const [checkinDone, setCheckinDone]     = useState(false);
  const [checkinMsg, setCheckinMsg]       = useState('');
  const [dailyFact, setDailyFact]         = useState(null);
  const [loadingFact, setLoadingFact]     = useState(false);
  const [showRelapse, setShowRelapse]     = useState(false);
  const [relapseData, setRelapseData]     = useState(null);
  const [relapseLoading, setRelapseLoading] = useState(false);
  const [weeklyReport, setWeeklyReport]   = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [eventTimer, setEventTimer]       = useState('');

  const xpNeeded = profile.level * 200;
  const xpPct    = Math.min(100, Math.round((profile.xp % xpNeeded) / xpNeeded * 100));

  // Countdown evento raro
  useEffect(() => {
    if (!profile.activeEvent || !profile.eventExpiresAt) return;
    const tick = () => {
      const left = profile.eventExpiresAt - Date.now();
      if (left <= 0) { setEventTimer(''); return; }
      const h = Math.floor(left / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      const s = Math.floor((left % 60000) / 1000);
      setEventTimer(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [profile.activeEvent, profile.eventExpiresAt]);

  useEffect(() => {
    setLoadingFact(true);
    api.dailyFact(userId)
      .then(res => {
        try { setDailyFact(JSON.parse(res.data.fact)); }
        catch { setDailyFact({ fatto: res.data.fact, emoji: '🌍', categoria: '' }); }
      })
      .catch(() => {})
      .finally(() => setLoadingFact(false));
  }, [userId]);

  async function doCheckin() {
    const res = await api.checkin(userId);
    const { profile: updated, alreadyDone, xpGained, rareEvent } = res.data;
    setProfile(updated);
    if (alreadyDone) {
      setCheckinMsg('Già fatto oggi! Torna domani 💪');
    } else {
      setCheckinMsg(`+${xpGained} XP! Streak: ${updated.streakDays}d 🔥`);
    }
    setCheckinDone(true);
  }

  async function doRelapse() {
    setRelapseLoading(true);
    try {
      const res = await api.relapse(userId);
      setRelapseData(res.data);
      if (res.data.profile) setProfile(res.data.profile);
    } finally {
      setRelapseLoading(false);
    }
  }

  async function loadSurpriseFact() {
	setLoadingFact(true);
	try {
	  const res = await api.surpriseFact(userId);
	  try { setDailyFact(JSON.parse(res.data.fact)); }
	  catch { setDailyFact({ fatto: res.data.fact, emoji: '🌍', categoria: '' }); }
	  setProfile(res.data.profile);
	} catch(e) {
	  alert(e.response?.data?.error || 'Errore');
	} finally {
	  setLoadingFact(false);
	}
  }

  async function loadWeeklyReport() {
    setLoadingReport(true);
    try {
      const res = await api.weeklyReport(userId);
      try { setWeeklyReport(JSON.parse(res.data.report)); }
      catch { setWeeklyReport({ titolo: 'Report', insight: res.data.report, prossima_sfida: '', voto_settimana: 'B' }); }
    } finally { setLoadingReport(false); }
  }

  const streakFires = Array.from({ length: MAX_STREAK_DISPLAY }, (_, i) => ({
    lit: i < Math.min(profile.streakDays, MAX_STREAK_DISPLAY), delay: i * 0.05
  }));

  let badges = [];
  try { badges = JSON.parse(profile.badges || '[]'); } catch {}

  const bounceActive = profile.bounceBackUntil && Date.now() < profile.bounceBackUntil;
  const eventActive  = profile.activeEvent && profile.eventExpiresAt && Date.now() < profile.eventExpiresAt;

  const gradeClass = (g) => {
  const n = parseFloat(g);
  if (n >= 9) return 'grade-a';
  if (n >= 7) return 'grade-b';
  if (n >= 5) return 'grade-c';
  return 'grade-d';
  };

  return (
    <div style={{ paddingTop: 8 }}>

      {/* Evento raro attivo */}
      {eventActive && (
        <div className="rare-event-banner">
          <span className="rare-event-icon">✨</span>
          <div className="rare-event-name">{profile.activeEvent?.replace(/_/g,' ')}</div>
          <div className="rare-event-desc">Doppio XP attivo!</div>
          {eventTimer && <div className="rare-event-timer">⏱ {eventTimer} rimanenti</div>}
        </div>
      )}

      {/* Bounce back attivo */}
      {bounceActive && (
        <div className="bounce-back-indicator">
          ⚡ BOUNCE BACK ATTIVO — 2x XP per le prossime ore!
        </div>
      )}

      {/* Avatar */}
      <div className="card">
        <div className="avatar-display">
          <div className="avatar-emoji">
            {AVATAR_EMOJIS[profile.avatarTier]}
            {profile.avatarTempRegressed && <span style={{fontSize:16,marginLeft:4}}>💔</span>}
          </div>
          <div className="avatar-name">
            {AVATAR_NAMES[profile.avatarTier]}
            {profile.avatarTempRegressed && <span style={{fontSize:13,color:'var(--accent)',display:'block'}}>temporaneamente regredito</span>}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>{profile.nickname}</div>
        </div>
        <div className="xp-bar-wrap">
          <div className="xp-bar-label">
            <span>Lv.{profile.level}</span>
            <span>{profile.xp % xpNeeded} / {xpNeeded} XP{bounceActive ? ' (2x🔥)' : ''}</span>
          </div>
          <div className="xp-bar-track">
            <div className="xp-bar-fill" style={{ width: xpPct + '%' }} />
          </div>
        </div>
        {badges.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="card-title">badge</div>
            <div className="badges-row">
              {badges.map((b, i) => <span key={i} className="badge">🏅 {b}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Streak */}
      <div className="streak-section">
        <div className="card-title" style={{ textAlign: 'center' }}>streak</div>
        <span className="streak-number">{profile.streakDays}</span>
        <div className="streak-fires">
          {streakFires.map((f, i) => (
            <span key={i} className={`streak-fire ${f.lit ? '' : 'dim'}`}
                  style={{ animationDelay: `${f.delay}s` }}>🔥</span>
          ))}
          {profile.streakDays > MAX_STREAK_DISPLAY && (
            <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14 }}>
              +{profile.streakDays - MAX_STREAK_DISPLAY}
            </span>
          )}
        </div>
        <div className="streak-label">
          {profile.streakDays === 0 && 'Inizia oggi il tuo streak!'}
          {profile.streakDays === 1 && 'Primo giorno — il viaggio comincia ⚔️'}
          {profile.streakDays >= 2 && profile.streakDays < 7 && `${profile.streakDays} giorni — continua!`}
          {profile.streakDays >= 7 && `${profile.streakDays} giorni — sei una macchina! 🔥`}
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={doCheckin} disabled={checkinDone}>
            {checkinDone ? checkinMsg : '🔥 Check-in (+30 XP)'}
          </button>
        </div>
      </div>

      {/* Corruzione Digitale */}
      <div className="corruption-section">
        <div className="card-title">⚡ stato mentale</div>

        <div className="corruption-bar-wrap">
          <div className="corruption-bar-header">
            <span className="corruption-bar-label">Attenzione Recuperata</span>
            <span className="corruption-bar-value" style={{color:'var(--xp)'}}>{profile.attentionScore ?? 20}%</span>
          </div>
          <div className="corruption-track">
            <div className="corruption-fill bar-attention" style={{width:(profile.attentionScore ?? 20)+'%'}} />
          </div>
        </div>

        <div className="corruption-bar-wrap">
          <div className="corruption-bar-header">
            <span className="corruption-bar-label">Tossicità Digitale</span>
            <span className="corruption-bar-value" style={{color:'var(--accent)'}}>{profile.toxicityScore ?? 80}%</span>
          </div>
          <div className="corruption-track">
            <div className="corruption-fill bar-toxicity" style={{width:(profile.toxicityScore ?? 80)+'%'}} />
          </div>
        </div>

        <div className="corruption-bar-wrap">
          <div className="corruption-bar-header">
            <span className="corruption-bar-label">Mente Libera</span>
            <span className="corruption-bar-value" style={{color:'var(--green)'}}>{profile.freedomScore ?? 10}%</span>
          </div>
          <div className="corruption-track">
            <div className="corruption-fill bar-freedom" style={{width:(profile.freedomScore ?? 10)+'%'}} />
          </div>
        </div>
      </div>

      {/* Fatto del giorno */}
      <div className="daily-fact">
        <div className="daily-fact-label">⚡ fatto sorprendente di oggi</div>
        {loadingFact ? (
          <div style={{color:'var(--muted)',fontSize:13}}>Generando curiosità...</div>
        ) : dailyFact ? (
          <>
            <span className="daily-fact-emoji">{dailyFact.emoji || '🌍'}</span>
            <div className="daily-fact-text">{dailyFact.fatto}</div>
            {dailyFact.categoria && <div className="daily-fact-cat">#{dailyFact.categoria}</div>}
			<button
			  style={{marginTop:12, padding:'8px 16px', background:'rgba(0,229,255,.12)', border:'1px solid var(--xp)', borderRadius:8, color:'var(--xp)', fontSize:12, fontWeight:700, cursor:'pointer'}}
			  onClick={loadSurpriseFact}
			  disabled={loadingFact || profile.xp < 10}
			>
			  {profile.xp < 10 ? '⚡ Servono 10 XP' : '⚡ Sorprendimi ancora (-10 XP)'}
			</button>
          </>
        ) : null}
      </div>

      {/* Weekly Report */}
      {!weeklyReport ? (
        <div className="card">
          <div className="card-title">📊 report settimanale</div>
          <button className="btn btn-secondary" onClick={loadWeeklyReport} disabled={loadingReport}>
            {loadingReport ? '⏳ Generando report...' : '📊 Genera Report Settimanale'}
          </button>
        </div>
      ) : (
        <div className="report-card">
          <div className="report-title">{weeklyReport.titolo}</div>
          <div className="report-insight">{weeklyReport.insight}</div>
          {weeklyReport.prossima_sfida && (
            <div className="report-challenge">⚔️ Prossima sfida: {weeklyReport.prossima_sfida}</div>
          )}
          <span className={`report-grade ${gradeClass(weeklyReport.voto_settimana)}`}>
            {weeklyReport.voto_settimana}
          </span>
        </div>
      )}

      {/* Stats */}
      <div className="card">
        <div className="card-title">statistiche</div>
        <div className="stats-grid">
          {[
            { val: profile.xp,               lbl: 'XP Totali',   color: 'var(--xp)' },
            { val: profile.level,             lbl: 'Livello',     color: 'var(--accent2)' },
            { val: profile.streakDays + 'd',  lbl: 'Streak',      color: 'var(--accent)' },
            { val: profile.relapseCount ?? 0, lbl: 'Relapse',     color: 'var(--muted)' },
          ].map(s => (
            <div key={s.lbl} className="stat-box">
              <div className="stat-val" style={{ color: s.color }}>{s.val}</div>
              <div className="stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Relapse button */}
      <button className="relapse-btn" onClick={() => setShowRelapse(true)}>
        💔 Ho ceduto — dichiara relapse
      </button>

      {/* Relapse modal */}
      {showRelapse && (
        <div className="relapse-modal-overlay" onClick={() => !relapseLoading && !relapseData && setShowRelapse(false)}>
          <div className="relapse-modal" onClick={e => e.stopPropagation()}>
            {!relapseData ? (
              <>
                <div className="relapse-modal-icon">💔</div>
                <div className="relapse-modal-title">Hai ceduto.</div>
                <div className="relapse-modal-text">
                  Lo streak si azzera. Il boss vince questa battaglia.<br/>
                  Ma dichiararlo è già un atto di coraggio.<br/>
                  <strong style={{color:'var(--xp)'}}>Bonus: 2x XP per 24 ore per rialzarti.</strong>
                </div>
                <button className="btn btn-primary" style={{marginBottom:12}} onClick={doRelapse} disabled={relapseLoading}>
                  {relapseLoading ? '⏳ ...' : '💔 Confermo — ho ceduto'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowRelapse(false)}>
                  Annulla
                </button>
              </>
            ) : (
              <>
                <div className="relapse-modal-icon">⚔️</div>
                <div className="relapse-modal-title">
                  {relapseData.alreadyDone ? 'Già dichiarato oggi' : 'Streak azzerato'}
                </div>
                {relapseData.bossMessage && (
                  <div className="boss-message">"{relapseData.bossMessage}"</div>
                )}
                {relapseData.bounceBack && (
                  <div className="bounce-back-badge">⚡ {relapseData.bounceBack}</div>
                )}
                <button className="btn btn-primary" onClick={() => { setShowRelapse(false); setRelapseData(null); }}>
                  Ricominciare ⚔️
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
